import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * BrandLogo — the compliance gate for Palo Alto Networks marks
 * (May 2026 Brand Guidelines, encoded in ../brand/brand-tokens.json).
 *
 * Enforced here, not by convention:
 *  - MINIMUM DIGITAL SIZE (p14/25): primary logo 100px, horizontal
 *    lockups 150px, vertical lockups 200px — requested widths below the
 *    floor are clamped up (symbols/icons floor at 24px per our audit).
 *  - CLEARSPACE (pp12/18): X-unit space on all sides, approximated as
 *    padding proportional to the rendered size, built into the element.
 *  - COLORWAY BY BACKGROUND (p15): both theme files render; CSS picks
 *    via [data-theme] — Positive-family on light, Negative-family on
 *    dark. No JS on theme flip, no flash, never a wrong-contrast mark.
 *  - NEVER TINTED (p13): renders kit artwork verbatim via <img> —
 *    currentColor can't touch it.
 *
 * Compact contexts: per p30, if the container can't fit the clamped
 * minimum, DON'T render a squeezed lockup — use variant 'icon' or the
 * parent 'panw' mark instead (the caller decides; this component will
 * not silently shrink below the floor).
 */
const MIN_WIDTH_PX = {
    logo: 100, // p14
    lockup: 150, // p25
    'tagline-lockup': 150,
    'by-line': 150,
    'vertical-lockup': 200, // p25
    'vertical-tagline-lockup': 200,
    icon: 24, // symbol floor (audit; guideline gives no px)
};
const warned = new Set();
export function BrandLogo({ sources, variant = 'logo', width, clearspace = true, alt = 'Palo Alto Networks', }) {
    const min = MIN_WIDTH_PX[variant] ?? 100;
    const w = Math.max(width ?? min, min);
    if (width !== undefined && width < min && !warned.has(variant)) {
        warned.add(variant);
        // eslint-disable-next-line no-console
        console.warn(`[BrandLogo] ${variant} requested at ${width}px — clamped to the `
            + `${min}px guideline minimum (Brand Guidelines p14/25). `
            + 'For smaller placements use GTM color coding, not a logo.');
    }
    // X-unit clearspace ≈ 8% of width on all sides (X = the logo's 'O').
    const pad = clearspace ? Math.round(w * 0.08) : 0;
    const light = sources.light ?? sources.dark;
    const dark = sources.dark ?? sources.light;
    if (!light && !dark)
        return null;
    return (_jsxs("span", { className: "brandlogo", style: { padding: pad }, children: [light && _jsx("img", { className: "brandlogo--light", src: light, width: w, alt: alt, loading: "lazy" }), dark && _jsx("img", { className: "brandlogo--dark", src: dark, width: w, alt: alt, loading: "lazy" })] }));
}
